import React from "react"
import {
  useAssignableQuizzes,
  useAssignablePeople,
  useAssignPreview,
  useCreateAssignment,
  useGroups,
} from "@/lib/queries"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Segmented, StepHeading } from "@/components/manager/AssignControls"
import { AssignSummary } from "@/components/manager/AssignSummary"
import { toast } from "sonner"
import { ApiError } from "@/lib/api"
import type { AssignPreview, AssignRequest, Group } from "@/lib/types"

type DueOption = "1" | "2" | "3" | "4" | "date" | "none"
type RepeatOption = "once" | "6" | "12"

const DUE_OPTIONS: { value: DueOption; label: string }[] = [
  { value: "1", label: "1 week" },
  { value: "2", label: "2 weeks" },
  { value: "3", label: "3 weeks" },
  { value: "4", label: "4 weeks" },
  { value: "date", label: "Date…" },
  { value: "none", label: "No due date" },
]

const REPEAT_OPTIONS: { value: RepeatOption; label: string }[] = [
  { value: "once", label: "Once" },
  { value: "6", label: "Every 6 months" },
  { value: "12", label: "Every year" },
]

export function AssignTab({ onAssigned }: { onAssigned: () => void }) {
  const { data: quizzes = [], isLoading: quizzesLoading } = useAssignableQuizzes()
  const { data: people = [], isLoading: peopleLoading } = useAssignablePeople()
  const { data: groups = [] } = useGroups()
  const createAssignment = useCreateAssignment()
  const previewMutation = useAssignPreview()

  const [selectedQuizIds, setSelectedQuizIds] = React.useState<Set<number>>(new Set())
  const [selectedUserIds, setSelectedUserIds] = React.useState<Set<number>>(new Set())
  const [dueOption, setDueOption] = React.useState<DueOption>("none")
  const [customDueDate, setCustomDueDate] = React.useState("")
  const [repeatOption, setRepeatOption] = React.useState<RepeatOption>("once")

  // The last preview, tagged with the request it answered. Assign is only
  // enabled when that request is the current one, and it sends that request.
  const [result, setResult] = React.useState<{
    key: string
    request: AssignRequest
    preview: AssignPreview | null
    error: string | null
  } | null>(null)

  // A chip is ticked only when it was clicked, in click order. Deriving it
  // from the selection would also tick any group whose members are a subset
  // of another ticked group, and a repeat would then schedule that group too.
  const [clickedGroupIds, setClickedGroupIds] = React.useState<number[]>([])

  const toggleGroup = (group: Group) => {
    const wasClicked = clickedGroupIds.includes(group.id)
    const remaining = clickedGroupIds.filter((id) => id !== group.id)
    const keep = new Set(
      groups.filter((g) => remaining.includes(g.id)).flatMap((g) => g.member_ids)
    )
    setClickedGroupIds(wasClicked ? remaining : [...clickedGroupIds, group.id])
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      group.member_ids.forEach((id) => {
        if (!wasClicked) next.add(id)
        else if (!keep.has(id)) next.delete(id)
      })
      return next
    })
  }

  const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<number>>>, id: number) =>
    setter((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  // Group mode: the selected people are exactly the union of the ticked
  // groups' members. Anything else is a list of individuals.
  const tickedGroups = clickedGroupIds
    .map((id) => groups.find((g) => g.id === id))
    .filter((g): g is Group => g !== undefined)
  const groupMemberIds = new Set(tickedGroups.flatMap((g) => g.member_ids))
  const groupMode =
    tickedGroups.length > 0 &&
    groupMemberIds.size === selectedUserIds.size &&
    Array.from(selectedUserIds).every((id) => groupMemberIds.has(id))
  const groupMemberNames = people
    .filter((p) => groupMemberIds.has(p.id))
    .map((p) => p.name)

  const effectiveRepeat: RepeatOption = groupMode ? repeatOption : "once"

  const request: AssignRequest = {
    quiz_ids: Array.from(selectedQuizIds),
    user_ids: groupMode ? [] : Array.from(selectedUserIds),
    group_ids: groupMode ? tickedGroups.map((g) => g.id) : [],
    due_in_weeks: ["1", "2", "3", "4"].includes(dueOption) ? Number(dueOption) : null,
    due_on: dueOption === "date" ? customDueDate || null : null,
    repeat_months: effectiveRepeat === "once" ? null : (Number(effectiveRepeat) as 6 | 12),
  }
  const ready = selectedQuizIds.size > 0 && selectedUserIds.size > 0
  const requestKey = JSON.stringify(request)

  // Debounced preview. Only the latest request's result is kept: a stale
  // response (or a cleared form) bumps the counter and is ignored.
  const latestRequest = React.useRef(0)
  const { mutateAsync: runPreview } = previewMutation
  React.useEffect(() => {
    const id = ++latestRequest.current
    if (!ready) {
      setResult(null)
      return
    }
    const sent = JSON.parse(requestKey) as AssignRequest
    const timer = setTimeout(async () => {
      let next: { preview: AssignPreview | null; error: string | null }
      try {
        next = { preview: await runPreview(sent), error: null }
      } catch (error) {
        next = {
          preview: null,
          error: error instanceof Error ? error.message : "Could not preview this assignment",
        }
      }
      if (id === latestRequest.current) {
        setResult({ key: requestKey, request: sent, ...next })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [requestKey, ready, runPreview])

  const current = result !== null && result.key === requestKey
  const previewLoading = ready && !current

  const allQuizzesSelected = quizzes.length > 0 && quizzes.every((q) => selectedQuizIds.has(q.id))
  const allPeopleSelected = people.length > 0 && people.every((p) => selectedUserIds.has(p.id))

  const handleAssign = async () => {
    try {
      if (!result || !current || !result.preview) return
      const created = await createAssignment.mutateAsync(result.request)
      // Staff the assessment is not available to are dropped by the server, and
      // a self-assignment in the same request still makes it a success. Saying
      // only "assigned successfully" would claim more than happened.
      const skipped = created.skipped_user_ids.length
      if (skipped > 0) {
        toast.warning(
          skipped === 1
            ? `${created.assignments.length} assigned. 1 person was skipped: they are not an active member of your organization.`
            : `${created.assignments.length} assigned. ${skipped} people were skipped: they are not active members of your organization.`
        )
      } else {
        toast.success(
          created.assignments.length === 1
            ? "Assessment assigned successfully"
            : `${created.assignments.length} assignments created`
        )
      }
      setSelectedQuizIds(new Set())
      setSelectedUserIds(new Set())
      setClickedGroupIds([])
      setDueOption("none")
      setCustomDueDate("")
      setRepeatOption("once")
      onAssigned()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Failed to assign assessment")
    }
  }

  return (
    <div className="space-y-6">
      <section className="grid gap-2">
        <StepHeading n={1}>Assessments</StepHeading>
        <div className="flex items-end justify-between">
          <Label>
            Assessments{" "}
            {selectedQuizIds.size > 0 && (
              <span className="font-normal text-muted-foreground">
                ({selectedQuizIds.size} selected)
              </span>
            )}
          </Label>
          {quizzes.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              onClick={() =>
                setSelectedQuizIds(allQuizzesSelected ? new Set() : new Set(quizzes.map((q) => q.id)))
              }
            >
              {allQuizzesSelected ? "Clear all" : "Select all"}
            </Button>
          )}
        </div>
        {quizzesLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : quizzes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No assessments available.</p>
        ) : (
          <ScrollArea className="h-40 rounded-md border p-2">
            {quizzes.map((q) => (
              <label
                key={q.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={selectedQuizIds.has(q.id)}
                  onChange={() => toggleIn(setSelectedQuizIds, q.id)}
                  className="h-4 w-4 rounded border-input"
                />
                <span className="font-medium">{q.title}</span>
              </label>
            ))}
          </ScrollArea>
        )}
      </section>

      <section className="grid gap-2">
        <StepHeading n={2}>Who</StepHeading>
        <div className="flex items-end justify-between">
          <Label>
            Assignees{" "}
            {selectedUserIds.size > 0 && (
              <span className="font-normal text-muted-foreground">
                ({selectedUserIds.size} selected)
              </span>
            )}
          </Label>
          {people.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              onClick={() =>
                setSelectedUserIds(allPeopleSelected ? new Set() : new Set(people.map((p) => p.id)))
              }
            >
              {allPeopleSelected ? "Clear all" : "Select all"}
            </Button>
          )}
        </div>
        {groups.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {groups.map((group) => (
              <label
                key={group.id}
                className="flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={clickedGroupIds.includes(group.id)}
                  onChange={() => toggleGroup(group)}
                  className="h-3.5 w-3.5 rounded border-input"
                />
                {group.name}
                <span className="text-muted-foreground">({group.member_count})</span>
              </label>
            ))}
          </div>
        )}
        {groupMemberNames.length > 0 && (
          <p className="text-xs text-muted-foreground">
            In selected groups: {groupMemberNames.join(", ")}
          </p>
        )}
        {peopleLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : people.length === 0 ? (
          <p className="text-sm text-muted-foreground">No one to assign to.</p>
        ) : (
          <ScrollArea className="h-40 rounded-md border p-2">
            {people.map((person) => (
              <label
                key={person.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={selectedUserIds.has(person.id)}
                  onChange={() => toggleIn(setSelectedUserIds, person.id)}
                  className="h-4 w-4 rounded border-input"
                />
                <span className="font-medium">{person.name}</span>
                {person.role === "manager" && (
                  <span className="text-muted-foreground">(Admin)</span>
                )}
                {person.name !== person.username && (
                  <span className="text-xs text-muted-foreground">@{person.username}</span>
                )}
              </label>
            ))}
          </ScrollArea>
        )}
      </section>

      <section className="grid gap-2">
        <StepHeading n={3}>Due</StepHeading>
        <Segmented label="Due" options={DUE_OPTIONS} value={dueOption} onChange={setDueOption} />
        {dueOption === "date" && (
          <div className="grid max-w-xs gap-2">
            <Label htmlFor="due-on">Due date</Label>
            <Input
              id="due-on"
              type="date"
              value={customDueDate}
              onChange={(e) => setCustomDueDate(e.target.value)}
            />
          </div>
        )}
      </section>

      <section className="grid gap-2">
        <StepHeading n={4}>Repeat</StepHeading>
        <Segmented
          label="Repeat"
          options={REPEAT_OPTIONS}
          value={effectiveRepeat}
          onChange={setRepeatOption}
          disabled={!groupMode}
        />
        {!groupMode && (
          <p className="text-xs text-muted-foreground">
            Repeat needs whole groups. Untick individual people or pick groups only.
          </p>
        )}
        {effectiveRepeat !== "once" && (
          <p className="text-xs text-muted-foreground">
            Repeats go to whoever is in the group at the time.
          </p>
        )}
      </section>

      <AssignSummary
        ready={ready}
        loading={previewLoading}
        error={current ? result.error : null}
        preview={result?.preview ?? null}
        current={current}
        assigning={createAssignment.isPending}
        onAssign={handleAssign}
        oneOffFromGroups={!groupMode && clickedGroupIds.length > 0}
      />
    </div>
  )
}
